import { IGoodInventoryProductFilterBody } from "@/api_framework/api_modals/Inventory";
import PaginationComponent from "@/app/components/ui/Pagination";
import { useDownloadSnapshotAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useDownloadSnapshotAPI";
import { useProductFilterAPI } from "@/app/hooks/api_hooks/Inventory/GoodInventory/useProductFilterAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useLoader } from "@/app/hooks/useLoader";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { useEffect, useRef, useState } from "react";
import { IoEllipsisHorizontalCircleOutline } from "react-icons/io5";
import InventoryMovementFilters from "../InventoryMovementFilters";
import RBACFlag from "@/app/components/core/RBACFlag";
import NoRecordFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useUtils } from "@/app/hooks/useUtils";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";

export interface IInventorySnapshotProps {
  clinics: string;
  category: Array<string>;
  productId: string;
  productName: string;
}

export interface ProductSearchByClinicIdResultType {
  vetbuddy_plan_item_name: string;
  vetic_variant_id: string;
}
const PageSize = 20;
const InventorySnapshot = () => {
  const [activeTab, setActiveTab] = useState("Overall");
  const [activeBadButton, setActiveBadButton] = useState("Expired");
  const [productFilters, setProductFilters] = useState<IGoodInventoryProductFilterBody>();
  const [activeRow, setActiveRow] = useState<number | null>(null);
  // RBAC: INVENTORY_SNAPSHOT_ROW_ACTION — legacy roles: clinic_ops, clinic_ops_doggyworld
  const canPerformSnapshotRowAction = useFeatureAccess("INVENTORY_SNAPSHOT_ROW_ACTION");
  const [_loading, setLoading] = useState(true);
  const { execute: productFilterAPI, data } = useProductFilterAPI();
  const [currentPage, setCurrentPage] = useState(1);
  const dropdownRef = useRef(null);
  const { setVisible: goodToBad, setModalData, isVisible } = useModalActions("GOOD_TO_BAD_MODAL");
  const isProductMoveGoodToBadInProgess = useLoader("isInventoryProductGoodInventory");
  let isClinicError = false;
  const {
    setVisible: shrink,
    setModalData: setShrinkModalData,
    isVisible: isShrinkVisible,
  } = useModalActions("SHRINK_MODAL");
  const modalState = useModalState("GOOD_TO_BAD_MODAL");
  const shrinkModalState = useModalState("SHRINK_MODAL");
  const [shouldCallAPI, setShouldCallAPI] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const { execute: snapshotDownload } = useDownloadSnapshotAPI();
  const [loader, setLoader] = useState(false);
  const { NO_PWL_RESULTS } = useImageConstants();
  const { veticDateFormat } = useUtils();

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setActiveRow(null);
    }
  };

  useEffect(() => {
    if (shouldCallAPI && productFilters?.clinic_ids?.length > 0) {
      const filterBody = {
        ...productFilters,
        clinic_ids: productFilters?.clinic_ids,
      } as IGoodInventoryProductFilterBody;
      if (!isClinicError && !filterBody.clinic_ids) {
        isClinicError = true;
        return;
      }
      let params = "";
      if (activeTab == "Good") {
        params = "good_inventory";
      }
      if (activeTab == "Bad") {
        params = "bad_inventory";
      }
      setLoading(true);
      productFilterAPI(filterBody, params, currentPage, PageSize).finally(() => {
        isClinicError = false;
        setLoading(false);
      });
    }
  }, [shouldCallAPI, productFilters, activeTab, refresh, currentPage, isVisible(), isShrinkVisible()]);

  const handleModalUpdate = () => {
    setRefresh((prev) => !prev);
    setShouldCallAPI(true);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getTableHeaders = (activeTab) => {
    const baseHeaders = [
      {
        key: "productName",
        label: "Product Name",
        className: "p-2 text-left whitespace-nowrap text-[10px] sm:text-xs 2xl:text-sm text-black font-medium",
      },
      {
        key: "clinicName",
        label: "Clinic Name",
        className: "p-2 text-left whitespace-nowrap text-[10px] sm:text-xs 2xl:text-sm text-black font-medium",
      },
      {
        key: "category",
        label: "Category",
        className: "px-6 py-3 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium",
      },
      {
        key: "lot",
        label: "Lot #",
        className: "px-6 py-3 text-left whitespace-nowrap text-[10px] sm:text-xs 2xl:text-sm text-black font-medium",
      },
      {
        key: "manufacturer",
        label: "Manufacturer",
        className: "px-6 py-3 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
      {
        key: "expDate",
        label: "Exp Date",
        className: "px-6 py-3 text-left whitespace-nowrap text-[10px] sm:text-xs 2xl:text-sm text-black font-medium",
      },
      {
        key: "mrp",
        label: "MRP (Tax Inclusive)",
        className:
          " whitespace-nowrap px-6 py-3 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
    ];
    const overallHeaders = [
      ...baseHeaders,
      {
        key: "goodQty",
        label: "Good / Sellable Qty",
        className:
          "  whitespace-nowrap p-2 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
      {
        key: "badQty",
        label: "Bad Qty",
        className:
          " whitespace-nowrap px-1 py-3 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
      {
        key: "blockedQty",
        label: "Blocked Qty",
        className:
          " whitespace-nowrap px-6 py-3 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
      {
        key: "totalQty",
        label: "Total Qty",
        className:
          " whitespace-nowrap px-6 py-3 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
    ];
    const goodHeaders = [
      ...baseHeaders,
      {
        key: "goodQty",
        label: "Good / Sellable Qty",
        className: "p-2 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
    ];
    if (activeTab == "Good" && canPerformSnapshotRowAction) {
      goodHeaders.push({
        key: "actionMenu",
        label: "Action Menu",
        className: "p-2 text-left text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      });
    }
    const badHeaders = [
      ...baseHeaders,
      {
        key: "qty",
        label: "Qty",
        className:
          "px-6 py-3 text-left whitespace-nowrap text-[10px] sm:text-xs 2xl:text-sm text-black font-medium sticky top-0",
      },
    ];
    switch (activeTab) {
      case "Overall":
        return overallHeaders;
      case "Good":
        return goodHeaders;
      case "Bad":
        return badHeaders;
      default:
        return baseHeaders;
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleBadButtonClick = (button) => {
    setActiveBadButton(button);
  };

  const handleIconClick = (rowIndex: number, goodInventoryQuantity: number, item) => {
    setShouldCallAPI(false);
    if (goodInventoryQuantity > 0) {
      setActiveRow(activeRow === rowIndex ? null : rowIndex);
      setModalData({
        ...modalState,
        status: false,
        vetic_variant_id: item.vetic_variant_id,
        unique_row_id: item?.unique_row_id,
        grn_id: item.grn_id,
        sub_grn_id: item.sub_grn_id,
        sub_category_id: item.sub_category_id,
      });
      setShrinkModalData({
        ...shrinkModalState,
        status: false,
        unique_row_id: item?.unique_row_id,
      });
    }
  };

  const handleMoveToGoodToBadClick = (item) => {
    setShouldCallAPI(false);
    setModalData({
      ...modalState,
      qty: item.good_inventory_quantity,
      unique_row_id: item?.unique_row_id,
      sub_category_id: item?.sub_category_id,
      product_name: item.product_name,
      lot_number: item.lot_number,
      manufacturer_name: item.manufacturer_name,
      expiry_date: item.expiry_date,
      mrp: item.mrp,
      status: false,
    });
    goodToBad();
    handleModalUpdate();
  };

  const handleMoveToShrinkClick = (item) => {
    setShouldCallAPI(false);
    setShrinkModalData({
      ...shrinkModalState,
      qty: item.good_inventory_quantity,
      unique_row_id: item?.unique_row_id,
      product_name: item.product_name,
      lot_number: item.lot_number,
      manufacturer_name: item.manufacturer_name,
      expiry_date: item.expiry_date,
      mrp: item.mrp,
    });
    shrink();
    handleModalUpdate();
  };

  const badButtonStyles = (button) => ({
    backgroundColor: activeBadButton === button ? "black" : "transparent",
    color: activeBadButton === button ? "white" : "black",
    border: "1px solid black",
    padding: "0.5rem 2rem",
    borderRadius: "1rem",
    cursor: "pointer",
    fontSize: "12px",
  });

  const tabStyles = (tab) => ({
    color: activeTab === tab ? "black" : "rgb(93,93,94)",
    borderBottom: activeTab === tab ? "2px solid black" : "none",
    cursor: "pointer",
    fontWeight: activeTab === tab ? "600" : "none",
  });

  const renderTableContent = () => {
    const headers = getTableHeaders(activeTab);
    return (
      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
        <tr className="py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize px-3">
          {headers?.map((header) => (
            <th key={header.key} className={header.className}>
              {header.label}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const handleExport = async () => {
    setLoader(true);
    const filterBody = {
      ...productFilters,
      clinic_ids: productFilters?.clinic_ids,
    };
    let params = "";
    if (activeTab === "Good") {
      params = "good_inventory";
    }
    if (activeTab === "Bad") {
      params = "bad_inventory";
    }
    await snapshotDownload(filterBody, params, currentPage, PageSize).then(() => {
      setLoader(false);
    });
  };

  const hasSnapshotData = Array.isArray(data?.data)
    ? data.data.length > 0
    : (data?.data?.[activeBadButton]?.length ?? 0) > 0;

  const formatRoundedQuantity = (value: number | string | null | undefined) => Math.round(Number(value) || 0);

  const paginationCounts = data?.inventory_with_counts?.pagination_counts;

  const totalCountMap = {
    Overall: paginationCounts?.all_inventory_count || 0,
    Good: paginationCounts?.good_inventory_count || 0,
    Bad: paginationCounts?.bad_inventory_count || 0,
  };

  return (
    <>
      <div className="rounded-md bg-white px-2 py-2">
        <h2 className="text-base font-semibold">Inventory Snapshot</h2>
        <InventoryMovementFilters setProductFilters={setProductFilters} />
      </div>
      <div className="rounded-md bg-white px-4 py-2">
        <div className="flex justify-between w-full bg-[rgb(249,249,249)]">
          <div className="flex gap-14  text-[rgb(93,93,94)] p-3">
            <p style={tabStyles("Overall")} onClick={() => handleTabClick("Overall")}>
              Overall ({formatRoundedQuantity(data?.inventory_with_counts?.count_of_all_inventory_quantity)})
            </p>
            <p style={tabStyles("Good")} onClick={() => handleTabClick("Good")}>
              Good ({formatRoundedQuantity(data?.inventory_with_counts?.count_of_good_inventory_quantity)})
            </p>
            <p style={tabStyles("Bad")} onClick={() => handleTabClick("Bad")}>
              Bad ({formatRoundedQuantity(data?.inventory_with_counts?.count_of_bad_inventory_quantity)})
            </p>
          </div>
          <div className="flex items-center">
            <RBACFlag actionId="INVENTORY_SNAPSHOT_EXPORT_AS_CSV">
              <button
                onClick={handleExport}
                disabled={loader}
                className={`text-white ${loader ? "bg-gray-400" : "bg-black"} py-2  text-[10px] sm:text-xs 2xl:text-sm px-4 border-transparent font-medium rounded-lg`}
              >
                {loader ? "Loading...." : "Export as .CSV"}
              </button>
            </RBACFlag>
          </div>
        </div>
        {activeTab === "Bad" && !Array.isArray(data?.data) && (
          <div className="flex gap-5 mt-2">
            <button
              style={badButtonStyles("Expired")}
              onClick={() => handleBadButtonClick("Expired")}
              className="text-xs"
            >
              Expired ({data?.data?.sub_counts?.expired_items || 0})
            </button>
            <button
              style={badButtonStyles("Damaged")}
              onClick={() => handleBadButtonClick("Damaged")}
              className="text-xs"
            >
              Damaged ({data?.data?.sub_counts?.damaged_items || 0})
            </button>
            <button
              style={badButtonStyles("Audit_Mismatch")}
              onClick={() => handleBadButtonClick("Audit_Mismatch")}
              className="text-xs"
            >
              Audit Mismatch ({data?.data?.sub_counts?.audit_mismatch_items || 0})
            </button>
          </div>
        )}
        <div>
          {isProductMoveGoodToBadInProgess ? (
            <TableSkeleton />
          ) : (
            <div>
              {hasSnapshotData ? (
                <div className="w-full mt-3">
                  <div className="w-full rounded-xl bg-[#F5F5F5] px-[3px] pb-[1px]">
                    <div className="w-full overflow-x-auto rounded-xl">
                      <div className="inline-block min-w-full py-1 align-middle">
                        <table className="min-w-max w-full border-separate border-spacing-x-0 border-spacing-y-0.5">
                          {renderTableContent()}
                          <tbody className="overflow-hidden rounded-xl w-full">
                            {activeTab !== "Bad" && Array.isArray(data?.data) ? (
                              data?.data.length > 0 ? (
                                data?.data.map((item, index) => (
                                  <tr key={index} className="group bg-white transition">
                                    <td
                                      className={
                                        "w-[220px] min-w-[220px] max-w-[220px] px-3 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left"
                                      }
                                    >
                                      <p className="whitespace-normal break-words">{item.product_name}</p>
                                    </td>
                                    <td
                                      className={
                                        "px-3 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                      }
                                    >
                                      <p>{item.clinic_name}</p>
                                    </td>
                                    <td
                                      className={
                                        "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                      }
                                    >
                                      <p>{item.category}</p>
                                    </td>
                                    <td
                                      className={
                                        "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                      }
                                    >
                                      <p>{item.lot_number}</p>
                                    </td>
                                    <td
                                      className={
                                        "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                      }
                                    >
                                      <p>{item.manufacturer_name}</p>
                                    </td>
                                    <td
                                      className={
                                        "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                      }
                                    >
                                      <p>{veticDateFormat(item.expiry_date)}</p>
                                    </td>
                                    <td
                                      className={
                                        "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                      }
                                    >
                                      <p>{item.mrp}</p>
                                    </td>
                                    {(activeTab == "Overall" || activeTab == "Good") && (
                                      <td
                                        className={
                                          "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                        }
                                      >
                                        <p>{formatRoundedQuantity(item.good_inventory_quantity)}</p>
                                      </td>
                                    )}
                                    {(activeTab == "Overall" || activeTab == "Bad") && (
                                      <td
                                        className={
                                          "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                        }
                                      >
                                        <p>{formatRoundedQuantity(item.bad_inventory_quantity)}</p>
                                      </td>
                                    )}
                                    {activeTab == "Overall" && (
                                      <td
                                        className={
                                          "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                        }
                                      >
                                        <p>{formatRoundedQuantity(item.blocked_inventory_quantity)}</p>
                                      </td>
                                    )}
                                    {activeTab == "Overall" && (
                                      <td
                                        className={
                                          "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                        }
                                      >
                                        <p>{formatRoundedQuantity(item.all_inventory_quantity)}</p>
                                      </td>
                                    )}
                                    {activeTab == "Good" && canPerformSnapshotRowAction && (
                                        <td className="px-6 py-4 whitespace-nowrap text-[10px] sm:text-xs 2xl:text-sm font-normal text-center relative">
                                          <IoEllipsisHorizontalCircleOutline
                                            fontSize={24}
                                            color="rgb(128,129,131)"
                                            className={`cursor-pointer ${item.good_inventory_quantity === 0 ? "pointer-events-none cursor-not-allowed " : ""}`}
                                            onClick={() => handleIconClick(index, item.good_inventory_quantity, item)}
                                          />
                                          {activeRow === index && item.good_inventory_quantity > 0 && (
                                            <div
                                              ref={dropdownRef}
                                              className="bg-white shadow-md p-3 z-10 w-48 rounded-sm absolute right-0 text-left"
                                            >
                                              <p
                                                onClick={() => handleMoveToGoodToBadClick(item)}
                                                className="pt-3 cursor-pointer"
                                              >
                                                Move from good to bad
                                              </p>
                                              {item?.is_shrinkable && (
                                                <p
                                                  onClick={() => handleMoveToShrinkClick(item)}
                                                  className="pt-3 cursor-pointer"
                                                >
                                                  Shrink
                                                </p>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                      )}
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td></td>
                                  <td
                                    colSpan={8}
                                    className="px-3 py-4 text-center text-gray-500 text-[10px] sm:text-xs 2xl:text-sm"
                                  >
                                    No results found
                                  </td>
                                </tr>
                              )
                            ) : data?.data?.[activeBadButton]?.length > 0 ? (
                              data?.data[activeBadButton].map((item, index) => (
                                <tr key={index}>
                                  <td
                                    className={
                                      "w-[220px] min-w-[220px] max-w-[220px] px-3 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left"
                                    }
                                  >
                                    <p className="whitespace-normal break-words">{item.product_name}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-3 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{item.clinic_name}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{item.category}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{item.lot_number}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{item.manufacturer_name}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{veticDateFormat(item.expiry_date)}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{item.mrp}</p>
                                  </td>
                                  <td
                                    className={
                                      "px-6 py-4 text-[10px] sm:text-xs 2xl:text-sm font-normal text-left whitespace-nowrap"
                                    }
                                  >
                                    <p>{formatRoundedQuantity(item.bad_inventory_quantity)}</p>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td></td>

                                <td colSpan={8} className="px-3 py-4 text-center text-[10px] sm:text-xs 2xl:text-sm ">
                                  No results found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  {productFilters?.clinic_ids && (
                    <div className="flex justify-center items-center w-auto shadow-md py-5">
                      <PaginationComponent
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={totalCountMap[activeTab] || 0}
                        pageSize={PageSize}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <NoRecordFoundComponent
                  imageURL={NO_PWL_RESULTS}
                  message="No Inventory Data Found"
                  description="No inventory data found for the given filters, try something else"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default InventorySnapshot;
